import React from 'react';
import {
  FaCampground,
  FaDrawPolygon,
  FaEye,
  FaEyeSlash,
  FaMap,
  FaMapMarkerAlt,
  FaPen,
  FaRoute,
  FaTrash,
} from 'react-icons/fa';
import { GiTreehouse } from 'react-icons/gi';
import { MdOutlineFeed } from 'react-icons/md';
import { clubFeederBarTone, clubFeederFillPercent } from '../utils/clubWaypointBoard';
import { clubFillCanLog } from '../utils/clubLogFill';
import {
  featureBoardChip,
  featureBoardFeederStatusLine,
  featureBoardMeta,
  featureBoardName,
  geoTypeOf,
  isFeatureHidden,
  isAreaGeometryType,
  isPersonalFeedLocation,
} from '../utils/featureBoardRows';

function FeatureBadgeIcon({ feature }) {
  const type = geoTypeOf(feature);
  if (isAreaGeometryType(type)) return <FaDrawPolygon />;
  if (type === 'LineString') return <FaRoute />;
  if (isPersonalFeedLocation(feature)) return <MdOutlineFeed />;
  const icon = String(feature?.icon || '').toLowerCase();
  if (icon === 'tree-stand') return <GiTreehouse />;
  if (icon === 'box-blind') return <FaCampground />;
  return <FaMapMarkerAlt />;
}

export function FeatureBoardRow({
  feature,
  hiddenFeatureIds,
  selectedFeatureId = null,
  hoveredFeatureId = null,
  onSelectFeature,
  onHoverFeature,
  onEditFeature,
  onStartGeometryEdit,
  onDeleteFeature,
  onToggleHidden,
  onLogFill,
  now,
}) {
  const hidden = isFeatureHidden(feature, hiddenFeatureIds);
  const selected =
    selectedFeatureId != null && Number(selectedFeatureId) === Number(feature.id);
  const hovered =
    hoveredFeatureId != null && Number(hoveredFeatureId) === Number(feature.id);
  const feeder = isPersonalFeedLocation(feature);
  const tone = feeder ? clubFeederBarTone(feature) : null;
  const chip = featureBoardChip(feature, { hidden, now });
  const canLog = feeder && clubFillCanLog(feature);
  const marker = geoTypeOf(feature) === 'Point' && !feeder && String(feature?.icon || '') !== 'tree-stand' && String(feature?.icon || '') !== 'box-blind' && !feature?.is_stand;

  const select = () => {
    if (onSelectFeature) onSelectFeature(feature);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      data-testid={`feature-row-${feature.id}`}
      className={[
        'feature-board-row',
        hidden ? 'feature-board-row--hidden' : '',
        tone === 'off' ? 'feature-board-row--off' : '',
        tone === 'low' ? 'feature-board-row--low' : '',
        tone === 'watch' ? 'feature-board-row--watch' : '',
        selected ? 'feature-board-row--selected' : '',
        hovered ? 'feature-board-row--hovered' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => onHoverFeature && onHoverFeature(feature.id)}
      onMouseLeave={() => onHoverFeature && onHoverFeature(null)}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      }}
    >
      <span
        className={`feature-board-row__badge${marker ? ' feature-board-row__badge--circle' : ''}${hidden ? ' feature-board-row__badge--hidden' : ''}`}
        aria-hidden="true"
      >
        <FeatureBadgeIcon feature={feature} />
      </span>
      <span className="feature-board-row__body">
        <span className="feature-board-row__name">{featureBoardName(feature)}</span>
        <span className="feature-board-row__meta">{featureBoardMeta(feature)}</span>
        {feeder && tone !== 'off' ? (
          <>
            <div className="club-feeder-card__bar" aria-hidden="true">
              <div
                className="club-feeder-card__bar-fill"
                data-testid={`feature-row-fill-${feature.id}`}
                data-tone={tone}
                style={{ width: `${clubFeederFillPercent(feature)}%` }}
              />
            </div>
            <span className="feature-board-row__feed">
              <span className="feature-board-row__status">
                {featureBoardFeederStatusLine(feature, now)}
              </span>
              {canLog ? (
                <button
                  type="button"
                  className="feature-board-row__log"
                  data-testid={`feature-row-log-${feature.id}`}
                  title="Log fill"
                  aria-label="Log fill"
                  onClick={(event) => {
                    event.stopPropagation();
                    onLogFill?.(feature);
                  }}
                >
                  LOG FILL
                </button>
              ) : null}
            </span>
          </>
        ) : null}
      </span>
      <span className={`feature-board-row__chip feature-board-row__chip--${chip.kind}`}>
        {chip.text}
      </span>
      <span
        className="feature-board-row__acts"
        onClick={(event) => event.stopPropagation()}
      >
        {typeof onToggleHidden === 'function' ? (
          <button
            type="button"
            className={`feature-board-row__act${hidden ? ' feature-board-row__act--gold' : ''}`}
            data-testid={`feature-row-hide-${feature.id}`}
            title={hidden ? 'Show feature' : 'Hide feature'}
            aria-label={hidden ? 'Show feature' : 'Hide feature'}
            onClick={() => onToggleHidden(feature)}
          >
            {hidden ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
          </button>
        ) : null}
        {typeof onStartGeometryEdit === 'function' ? (
          <button
            type="button"
            className="feature-board-row__act"
            data-testid={`feature-row-map-edit-${feature.id}`}
            title="Map edit (edit drawing)"
            aria-label="Map edit (edit drawing)"
            onClick={() => onStartGeometryEdit(feature)}
          >
            <FaMap aria-hidden="true" />
          </button>
        ) : null}
        {typeof onEditFeature === 'function' ? (
          <button
            type="button"
            className="feature-board-row__act feature-board-row__act--primary"
            data-testid={`feature-row-edit-${feature.id}`}
            title="Edit details"
            aria-label="Edit details"
            onClick={() => onEditFeature(feature)}
          >
            <FaPen aria-hidden="true" />
          </button>
        ) : null}
        {typeof onDeleteFeature === 'function' ? (
          <button
            type="button"
            className="feature-board-row__act feature-board-row__act--danger"
            data-testid={`feature-row-delete-${feature.id}`}
            title="Delete"
            aria-label="Delete"
            onClick={() => onDeleteFeature(feature)}
          >
            <FaTrash aria-hidden="true" />
          </button>
        ) : null}
      </span>
    </div>
  );
}

export default FeatureBoardRow;
